import type { 
  ChatRequest, 
  SuggestionsRequest, 
  SuggestionsResponse, 
  ProjectConfigResponseZod 
} from '@kwry/common'

// Rate limit information extracted from response headers
export interface RateLimitInfo {
  remaining: {
    perMinute: number
    perHour: number
    perDay: number
  }
  limits: {
    perMinute: number
    perHour: number
    perDay: number
  }
  resetTime: {
    perMinute: Date
    perHour: Date
    perDay: Date
  }
  retryAfter?: number // seconds to wait before next request
}

// Rate limit error thrown when 429 is received
export class RateLimitError extends Error {
  constructor(
    message: string,
    public rateLimitInfo: RateLimitInfo,
    public retryAfter: number
  ) {
    super(message)
    this.name = 'RateLimitError'
  }
}

// Base API configuration from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

/**
 * Standard headers sent with all API requests from iframe widget:
 * - Content-Type: application/json
 * - X-Project-ID: {projectId} - Identifies the tenant's project
 * - X-SDK-Origin: {parent page origin} - Domain validation for security
 */

// Helper function to get the parent page's origin
function getParentOrigin(): string {
  try {
    // Try to access parent origin directly (may fail due to cross-origin restrictions)
    return window.parent.location.origin
  } catch (error) {
    // Fallback to document.referrer if cross-origin access is blocked
    if (document.referrer) {
      try {
        return new URL(document.referrer).origin
      } catch (urlError) {
        console.warn('Could not parse referrer URL:', document.referrer)
      }
    }
    
    // Final fallback to current origin if all else fails
    console.warn('Could not determine parent origin, using current origin as fallback')
    return window.location.origin
  }
}

export class ApiClient {
  private readonly projectId: string
  private readonly headers: Record<string, string>
  private currentRateLimitInfo: RateLimitInfo | null = null
  private threadId: string | null = null

  constructor(projectId: string, threadId?: string) {
    this.projectId = projectId
    this.threadId = threadId || null
    
    // Compute headers once - they're static for the instance lifetime
    this.headers = {
      'Content-Type': 'application/json',
      'X-Project-Id': projectId,
      'X-SDK-Origin': getParentOrigin(),
    }

    // Add threadId to headers if provided
    if (this.threadId) {
      this.headers['X-Thread-ID'] = this.threadId
    }
  }

  // Set thread ID after initialization (for when it's generated later)
  setThreadId(threadId: string): void {
    this.threadId = threadId
    this.headers['X-Thread-ID'] = threadId
  }

  // Get the computed headers (for use in useChat, etc.)
  getHeaders(): Record<string, string> {
    return this.headers
  }

  // Get project ID
  getProjectId(): string {
    return this.projectId
  }

  // Get API base URL
  getApiBaseUrl(): string {
    return API_BASE_URL
  }

  // Get current rate limit information
  getRateLimitInfo(): RateLimitInfo | null {
    return this.currentRateLimitInfo
  }

  // Extract rate limit info from response headers
  private extractRateLimitInfo(response: Response): RateLimitInfo | null {
    try {
      // Check if rate limit headers are present
      const hasRateLimitHeaders = response.headers.has('X-RateLimit-Remaining-Minute') ||
                                  response.headers.has('X-RateLimit-Remaining-Hour') ||
                                  response.headers.has('X-RateLimit-Remaining-Day');
      
      if (!hasRateLimitHeaders) {
        // No rate limit headers present, don't show rate limit status
        return null;
      }

      const rateLimitInfo: RateLimitInfo = {
        remaining: {
          perMinute: parseInt(response.headers.get('X-RateLimit-Remaining-Minute') || '0'),
          perHour: parseInt(response.headers.get('X-RateLimit-Remaining-Hour') || '0'),
          perDay: parseInt(response.headers.get('X-RateLimit-Remaining-Day') || '0')
        },
        limits: {
          perMinute: parseInt(response.headers.get('X-RateLimit-Limit-Minute') || '10'),
          perHour: parseInt(response.headers.get('X-RateLimit-Limit-Hour') || '100'),
          perDay: parseInt(response.headers.get('X-RateLimit-Limit-Day') || '1000')
        },
        resetTime: {
          perMinute: new Date(parseInt(response.headers.get('X-RateLimit-Reset-Minute') || '0') * 1000),
          perHour: new Date(parseInt(response.headers.get('X-RateLimit-Reset-Hour') || '0') * 1000),
          perDay: new Date(parseInt(response.headers.get('X-RateLimit-Reset-Day') || '0') * 1000)
        }
      }

      const retryAfter = response.headers.get('Retry-After')
      if (retryAfter) {
        rateLimitInfo.retryAfter = parseInt(retryAfter)
      }

      return rateLimitInfo;
    } catch (error) {
      console.warn('Failed to extract rate limit info from headers:', error)
    }
    
    return null
  }

  // Generic fetch wrapper with error handling and rate limit awareness
  private async fetch<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.headers,
        ...options.headers,
      },
    })

    // Extract and store rate limit info from any response (success or error)
    const rateLimitInfo = this.extractRateLimitInfo(response)
    if (rateLimitInfo) {
      this.currentRateLimitInfo = rateLimitInfo
    }



    // Handle rate limit error (429)
    if (response.status === 429) {
      const errorData = await response.json().catch(() => ({}))
      const retryAfter = rateLimitInfo?.retryAfter || parseInt(response.headers.get('Retry-After') || '60')
      
      throw new RateLimitError(
        errorData.message || 'Rate limit exceeded. Please try again later.',
        rateLimitInfo || {
          remaining: { perMinute: 0, perHour: 0, perDay: 0 },
          limits: { perMinute: 10, perHour: 100, perDay: 1000 },
          resetTime: {
            perMinute: new Date(Date.now() + 60 * 1000),
            perHour: new Date(Date.now() + 60 * 60 * 1000),
            perDay: new Date(Date.now() + 24 * 60 * 60 * 1000)
          },
          retryAfter
        },
        retryAfter
      )
    }

    // Handle other errors
    if (!response.ok) {
      throw new Error(
        `API request failed: ${response.status} ${response.statusText}`
      )
    }

    return response.json()
  }

  // SDK Chat API
  async sendChatMessage(data: ChatRequest): Promise<any> {
    return this.fetch('/chat', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // SDK Suggestions API
  async getSuggestions(data: SuggestionsRequest): Promise<SuggestionsResponse> {
    return this.fetch('/suggestions', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // SDK Project Configuration API
  async getProject(): Promise<ProjectConfigResponseZod> {
    return this.fetch(`/project`)
  }
}

// Create API client with project ID
export const createApiClient = (projectId: string, threadId?: string): ApiClient => {
  return new ApiClient(projectId, threadId)
} 